import { BrandMark } from "./brand/BrandMark";

// CricketCompass brand identity (#186).
//
// THE MARK (#1153, D307, D313): the icon is Martin's traced logo, drawn by
// `brand/BrandMark.jsx` (provenance there). The 2026-08 hand-redraw that used
// to live here is gone. D307 is a recorded D217 exception limited to the logo
// artwork: the old look's layout, copy and every other pixel stay as they were.
//
// Brand guide references:
//   §3  dark-background variant is white ring + green; light surfaces get
//       the navy-and-green variant. Never recolour beyond that pair.
//   §4  Navy #0D1B3E, Green #5D9D35.
//   §5  wordmark is always "Cricket" in Navy immediately followed by
//       "Compass" in Green, one word, no space.

const NAVY = "#0D1B3E";
const GREEN = "#5D9D35";

/** The icon alone: square, `size`x`size`, drawn by the one `BrandMark`. */
function CompassMark({ size, variant }) {
  return <BrandMark size={size} variant={variant} testId="compass-logo-mark" />;
}

/** The wordmark, per brand guide §5 — one word, two colours, no space. */
function Wordmark({ fontSize, variant, className = "" }) {
  const first = variant === "dark" ? "#FFFFFF" : NAVY;
  return (
    <span
      className={`font-display font-black tracking-tight ${className}`}
      style={{ fontSize }}
    >
      <span style={{ color: first }}>Cricket</span>
      <span style={{ color: GREEN }}>Compass</span>
    </span>
  );
}

/**
 * The eyebrow line under the wordmark — the brand's three-word promise.
 *
 * Tracking is 0.10em, not the "wide" of brand guide §5. At 27 characters this
 * line is the widest thing in the lockup, and the topbar renders the lockup at
 * 390px next to the bell, the accessibility menu and the avatar. Wider tracking
 * pushed `topbar-logo` to 403px on a 390px viewport and made every phone-390
 * cell in the responsive harness scroll horizontally.
 */
function Eyebrow({ fontSize, variant, className = "" }) {
  return (
    <span
      className={`uppercase tracking-[0.1em] ${className}`}
      style={{ fontSize, color: variant === "dark" ? "#FFFFFF" : NAVY }}
    >
      Navigate. Develop. Perform.
    </span>
  );
}

export function CompassLogo({ size = 32, className = "", stacked = false, variant = "light", compact = false }) {
  const height = size;
  const gap = Math.max(2, Math.round(size * 0.06));
  const wordSize = Math.max(11, Math.round(size * 0.22));
  // 0.11 rather than the old 0.12: at the topbar's size=72 that is the
  // difference between an 8px and a 9px eyebrow, and 9px overflowed phone-390.
  // Every size the app actually uses (40/44/52/64/72) lands on 8px.
  const subSize = Math.max(8, Math.round(size * 0.11));

  // #785 (moved from #780) — the topbar's own anatomy, not a smaller copy of
  // the full lockup. `CompassLogo size={72}` renders the two-tone
  // "CricketCompass" wordmark plus the Eyebrow tagline, both `truncate`d to
  // fit — which is exactly what clipped mid-word at phone-390
  // ("CricketC…" / "NAVIGATE. DEVEL…"). SetUp.dc.html:34-35 draws the
  // header as a 22px mark next to a single word ("Compass", one colour, no
  // tagline) precisely because the full lockup does not fit a phone, and
  // #780's decision thread found that not deferrable to a future desktop
  // pass: it is broken on the device the whole redraw was drawn for, today.
  //
  // A THIRD IMPLEMENTATION OF THE SAME ANATOMY, DELIBERATELY, NOT A REUSE OF
  // `kit/TopBar.jsx`'s "white" variant — which already draws exactly this
  // (mark + "Compass", `var(--nw-navy)`, gap 8) for the same set-up screens.
  // `OnboardingNew.jsx`'s own docstring says why that component can't be
  // reused wholesale in `Layout.jsx`: `TopBar` always renders its OWN
  // decorative bell/info/avatar placeholders, none of them wired to the
  // real `NotificationsBell` / `AccessibilityMenu` / `AccountMenu` the
  // signed-in shell needs — rendering it here would stack a second,
  // non-functional bar under the first. So the mark+word PATTERN is
  // reimplemented at this one other call site rather than the whole
  // component, and it takes the kit's VALUES rather than inventing its
  // own: `gap: 8` (fixed, not `size`-scaled — the kit's is fixed too) and
  // `var(--nw-navy)` (`#0B2E59`), not this file's own pre-kit `NAVY`
  // constant (`#0D1B3E`, brand guide §4) — two different navys existed
  // before this fix and the topbar was drawn against the kit's. The mark
  // itself (`CompassMark`) is untouched: `TopBar.jsx` reuses `CompassLogo`
  // for the ring/ball too and accepts its existing colourway there, by its
  // own docstring ("reused here for the ring/ball per the issue, rather
  // than redrawing a mark") — only the WORDMARK text is a kit value.
  if (compact) {
    const compactWordSize = Math.max(13, Math.round(size * 0.73));
    return (
      <div
        className={`inline-flex items-center min-w-0 max-w-full ${className}`}
        style={{ gap: 8 }}
        data-testid="site-logo"
        aria-label="CricketCompass"
      >
        <CompassMark size={height} variant={variant} />
        <span
          className="font-display font-extrabold truncate"
          style={{ fontSize: compactWordSize, color: variant === "dark" ? "#FFFFFF" : "var(--nw-navy)" }}
        >
          Compass
        </span>
      </div>
    );
  }

  if (stacked) {
    return (
      <div
        className={`flex flex-col items-center min-w-0 max-w-full ${className}`}
        data-testid="site-logo"
        aria-label="CricketCompass"
      >
        <CompassMark size={height} variant={variant} />
        <Wordmark
          fontSize={wordSize}
          variant={variant}
          className="mt-2 max-w-full truncate"
        />
        <Eyebrow fontSize={subSize} variant={variant} className="max-w-full truncate" />
      </div>
    );
  }

  return (
    <div
      className={`inline-flex items-center min-w-0 max-w-full ${className}`}
      style={{ gap }}
      data-testid="site-logo"
      aria-label="CricketCompass"
    >
      <CompassMark size={height} variant={variant} />
      {size >= 44 && (
        <div className="flex flex-col leading-tight min-w-0 truncate">
          <Wordmark fontSize={wordSize} variant={variant} className="truncate" />
          <Eyebrow fontSize={subSize} variant={variant} className="truncate" />
        </div>
      )}
    </div>
  );
}
